/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: comp_projects entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the comp_projects table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the comp_projects API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, subject_property_id, comp_type_id, review_status_id, title, description, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, request_status_id, origin, escalated_at, subject_property_freetext, subject_property_freetext_hint, requested_deadline, submitted_at, first_touched_at, fulfilled_at, assigned_user_profile_id, assigned_at, assigned_by_user_profile_id, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id]
 * Searchable fields: []
 * Filterable fields: [id, subject_property_id, comp_type_id, review_status_id, created_by_user_profile_id, updated_by_user_profile_id, request_status_id, assigned_user_profile_id, assigned_by_user_profile_id, archived_by_user_profile_id, archive_batch_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  CompProjectsRow,
  CompProjectsInsert,
  CompProjectsUpdate
} from '../../types/generated_schemas/generated-comp_projects-schema.js';

export interface CompProjectsQueryOptions extends UnifiedQueryOptions {}

export interface CompProjectsResponse {
  data: CompProjectsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for comp_projects table operations
 *
 * @description Provides type-safe access to comp_projects API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class CompProjectsClient {
  private readonly basePath = '/comp-projects';

  /**
   * Get all comp_projects records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of comp_projects records
   */
  async getAll(options?: CompProjectsQueryOptions): Promise<CompProjectsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<CompProjectsResponse>(url);
    return response.data;
  }

  /**
   * Get a single comp_projects record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the comp_projects record
   */
  async getById(id: string): Promise<CompProjectsRow> {
    const response = await httpClient.get<{
      data: CompProjectsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new comp_projects record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created comp_projects record
   */
  async create(data: CompProjectsInsert): Promise<CompProjectsRow> {
    const response = await httpClient.post<{
      data: CompProjectsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing comp_projects record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated comp_projects record
   */
  async update(id: string, data: CompProjectsUpdate): Promise<CompProjectsRow> {
    const response = await httpClient.put<{
      data: CompProjectsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a comp_projects record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple comp_projects records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created comp_projects records
   */
  async createMany(data: CompProjectsInsert[]): Promise<CompProjectsRow[]> {
    const response = await httpClient.post<{
      data: CompProjectsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search comp_projects with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: CompProjectsQueryOptions
  ): Promise<CompProjectsResponse> {
    throw new Error('Search operations are not available for comp_projects - no searchable fields configured');
  }

  /**
   * Get count of comp_projects records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a comp_projects record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'comp_projects',
      publicFields: ['id', 'subject_property_id', 'comp_type_id', 'review_status_id', 'title', 'description', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'created_at', 'updated_at', 'request_status_id', 'origin', 'escalated_at', 'subject_property_freetext', 'subject_property_freetext_hint', 'requested_deadline', 'submitted_at', 'first_touched_at', 'fulfilled_at', 'assigned_user_profile_id', 'assigned_at', 'assigned_by_user_profile_id', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      searchableFields: [],
      filterableFields: ['id', 'subject_property_id', 'comp_type_id', 'review_status_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'request_status_id', 'assigned_user_profile_id', 'assigned_by_user_profile_id', 'archived_by_user_profile_id', 'archive_batch_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      requiredFields: ['id'],
    };
  }
}

// Export singleton instance for convenience
export const compProjectsClient = new CompProjectsClient();
